import { isEqual } from 'date-fns';

import fillBackWeek from './fillBackWeek';
import fillFrontWeek from './fillFrontWeek';
import getToday from './getToday';
import getWeeks from './getWeeks';
import isSelectable from './isSelectable';
import isSelected from './isSelected';
import {
  BlankCalendarDay,
  DateObj,
  DatePickerDate,
  CalendarMonth,
} from '../types';

function getNumDaysMonthYear(month: number, year: number) {
  const normalizedMonthYear = new Date(year, month, 1);
  month = normalizedMonthYear.getMonth();
  year = normalizedMonthYear.getFullYear();

  const daysInMonth = 32 - new Date(year, month, 32).getDate();
  return { daysInMonth, month, year };
}

export default function getMonths({
  month,
  year,
  selectedDates,
  minDate,
  maxDate,
  firstDayOfWeek,
  showOutsideDays,
  isDateSelectable,
  timeZone,
}: {
  month: number;
  year: number;
  selectedDates: (DatePickerDate | null)[] | null;
  minDate?: DatePickerDate | null;
  maxDate?: DatePickerDate | null;
  firstDayOfWeek: number;
  showOutsideDays?: boolean;
  isDateSelectable?: ({ date }: { date: Date }) => boolean;
  timeZone?: string;
}): CalendarMonth {
  // Get the normalized month and year, along with days in the month.
  const {
    daysInMonth,
    month: nMonth,
    year: nYear,
  } = getNumDaysMonthYear(month, year);

  // Fill out the dates for the month.
  const dates: (BlankCalendarDay | DateObj)[] = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(nYear, nMonth, day);

    const isDateSelectableFromProps = isDateSelectable
      ? isDateSelectable({ date })
      : true;

    const dateObj = {
      date,
      selected: isSelected(selectedDates, date),
      today: isEqual(getToday(timeZone), date),
      prevMonth: false,
      nextMonth: false,
      selectable:
        isSelectable({ minDate, maxDate, date }) && isDateSelectableFromProps,
    };

    dates.push(dateObj);
  }

  const firstDayOfMonth = new Date(nYear, nMonth, 1);
  const lastDayOfMonth = new Date(nYear, nMonth, daysInMonth);

  const frontWeekBuffer = fillFrontWeek({
    firstDayOfMonth,
    minDate,
    maxDate,
    selectedDates,
    firstDayOfWeek,
    showOutsideDays,
  });

  dates.unshift(...frontWeekBuffer);

  const backWeekBuffer = fillBackWeek({
    lastDayOfMonth,
    minDate,
    maxDate,
    selectedDates,
    calendarLength: dates.length,
    showOutsideDays,
  });

  dates.push(...backWeekBuffer);

  // Get the filled out weeks for the
  // given dates.
  const weeks = getWeeks(dates);

  // return the calendar data.
  return {
    firstDayOfMonth,
    lastDayOfMonth,
    month: nMonth,
    year: nYear,
    weeks,
  };
}
